import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Container,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { axiosRequests } from "../../app/api/axiosRequest";
import { toast } from "react-toastify";
 
type ActionType = "CLIENT" | "TARGET" | null;
 
const greenButtonStyle = {
  minWidth: 120,
  backgroundColor: "#2e7d32",
  "&:hover": {
    backgroundColor: "#1b5e20",
  },
};
 
const BeansIntegration = () => {
  const [clientId, setClientId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState<ActionType>(null);
 
  /* ---------- API Trigger ---------- */
  const handleConfirm = async (type: ActionType) => {
    if (!type) return;
 
    setActionType(type);
    setLoading(true);
 
    try {
      let response: string;
 
      if (type === "CLIENT") {
        response = await axiosRequests.getRouteStatus<string>(
          `/fdc/trigger/beans/${clientId}`
        );
      } else {
        response = await axiosRequests.getRouteStatus<string>(
          `/fdc/beans/sync/route/${targetId}`
        );
      }
 
      toast.success(response);
      setClientId("");
      setTargetId("");
    } catch (error: any) {
      const statusCode = error?.code;
      const errorMessage = error?.message;
 
      if (statusCode === "400" && errorMessage) {
        toast.error(errorMessage);
      } else if (statusCode === "401" || statusCode === "403") {
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Request failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
 
  return (
    <>
      {/* ---------- FULL SCREEN LOADER ---------- */}
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 999,
          backgroundColor: "rgba(0, 0, 0, 0.35)",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#2e7d32" }} />
      </Backdrop>
 
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Typography
          variant="h4"
          sx={{
            color: "#2e7d32",
            fontWeight: "bold",
            mb: 4,
          }}
        >
          BEANS INTEGRATION
        </Typography>
 
        <Stack spacing={4}>
          {/* ---------- Manual Beans Ingestion ---------- */}
          <Box
            sx={{
              border: "2px solid #2e7d32",
              borderRadius: "10px",
              p: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 3, color: "#2e7d32", fontWeight: "bold" }}
            >
              Ingest New Route
            </Typography>
 
            <Box display="flex" gap={2} maxWidth={600}>
              <TextField
                label="Enter Route ID"
                fullWidth
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              />
              <Button
                variant="contained"
                sx={greenButtonStyle}
                disabled={!clientId.trim() || loading}
                onClick={() => handleConfirm("CLIENT")}
              >
                Submit
              </Button>
            </Box>
          </Box>
 
          {/* ---------- Manual Route Optimization ---------- */}
          <Box
            sx={{
              border: "2px solid #2e7d32",
              borderRadius: "10px",
              p: 4,
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 3, color: "#2e7d32", fontWeight: "bold" }}
            >
              Optimize Route
            </Typography>
 
            <Box display="flex" gap={2} maxWidth={600}>
              <TextField
                label="Enter Route ID"
                fullWidth
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
              />
              <Button
                variant="contained"
                sx={greenButtonStyle}
                disabled={!targetId.trim() || loading}
                onClick={() => handleConfirm("TARGET")}
              >
                Submit
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </>
  );
};
 
export default BeansIntegration;